/* eslint react/display-name: 0, react/no-multi-comp: 0 */
import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { Icon } from '@scentregroup/ui'
import algoliasearch from 'algoliasearch'
import { CONFIG_KEYS, getClientConfigValue } from '../../helpers/config-helper'
import { distancePerState } from '../../helpers/distance-per-state'
import analyticsUtils from '../../helpers/analytics-utils'
import { getNowInCentreInTimeFormat } from '../../helpers/date-helpers'
import { VIEW_OPTION_MEMBERS_ONLY } from '../../constants'
import SearchResult from './search-result'
import { CentreType } from './types'
import SearchView from './search-view'
import style from './index.scss'

const HEADINGS = {
  services: 'Centre services',
  topStores: 'Top Stores',
  events: 'Events',
  offers: 'Offers',
  stories: 'Story',
  news: 'News',
  centres: 'Centres',
  nearbyStores: 'Other nearby stores',
  otherStores: 'Other stores near',
}

const hrefForStore = (centreSlug, store) =>
  `/${centreSlug}/store/${store.objectID}/${store.slug}`

// The queries we run whenever text changes, and how the results turn into dom.
// Everything here is passed to algolia except for the render method.

const centreWithDateFilter = (centreID, getOffsetlessTimeInCentre) => ({
  filters: `centres:${centreID} AND (showAt<${getOffsetlessTimeInCentre()} AND hideAt>${getOffsetlessTimeInCentre()})`,
})

const centreWithDateAndViewOptionFilter = (
  centreID,
  viewOptionToExclude,
  getOffsetlessTimeInCentre
) => ({
  filters: `centres:${centreID} AND (NOT viewOption:"${viewOptionToExclude}") AND (showAt<${getOffsetlessTimeInCentre()} AND hideAt>${getOffsetlessTimeInCentre()})`,
})

const singleCentreWithDateFilter = (centreID, getOffsetlessTimeInCentre) => ({
  filters: `centre:${centreID} AND (showAt<${getOffsetlessTimeInCentre()} AND hideAt>${getOffsetlessTimeInCentre()})`,
})

const getQueries = (
  query,
  centre,
  onResultClick,
  getOffsetlessTimeInCentre
) => [
  {
    heading: HEADINGS.topStores,
    indexName: 'store',
    query,
    params: singleCentreWithDateFilter(
      centre.sys.id,
      getOffsetlessTimeInCentre
    ),
    render: hit => (
      <SearchResult
        key={hit.objectID}
        href={hrefForStore(centre.slug, hit)}
        imageUrl={hit.img}
        highlightField="titleWithOptionalStatus"
        hit={hit}
        className={style.storeRow}
        onClick={() => onResultClick(HEADINGS.topStores, hit)}
      />
    ),
  },
  {
    heading: HEADINGS.services,
    indexName: 'site',
    query,
    render: hit => (
      <SearchResult
        key={hit.objectID}
        href={(hit.href || '').replace('CENTRE', centre.slug)}
        imageUrl={hit.img}
        highlightField="text"
        hit={hit}
        onClick={() => onResultClick(HEADINGS.services, hit)}
      />
    ),
  },
  {
    heading: HEADINGS.services,
    indexName: 'service',
    query,
    params: singleCentreWithDateFilter(
      centre.sys.id,
      getOffsetlessTimeInCentre
    ),
    render: hit => (
      <SearchResult
        key={hit.objectID}
        href={`/${centre.slug}/service/${hit.objectID}/${hit.slug}`}
        imageUrl={hit.img}
        highlightField="title"
        hit={hit}
        onClick={() => onResultClick(HEADINGS.services, hit)}
      />
    ),
  },
  {
    heading: HEADINGS.offers,
    indexName: 'offer',
    query,
    params: centreWithDateAndViewOptionFilter(
      centre.sys.id,
      VIEW_OPTION_MEMBERS_ONLY,
      getOffsetlessTimeInCentre
    ),
    render: hit => (
      <SearchResult
        key={hit.objectID}
        href={`/${centre.slug}/offer/${hit.objectID}/${hit.slug}`}
        imageUrl={hit.img}
        highlightField="title"
        hit={hit}
        description={hit.storeTitles.join(', ')}
        onClick={() => onResultClick(HEADINGS.offers, hit)}
      />
    ),
  },
  {
    heading: HEADINGS.events,
    indexName: 'event',
    query,
    params: centreWithDateAndViewOptionFilter(
      centre.sys.id,
      VIEW_OPTION_MEMBERS_ONLY,
      getOffsetlessTimeInCentre
    ),
    render: hit => (
      <SearchResult
        key={hit.objectID}
        href={`/${centre.slug}/event/${hit.objectID}/${hit.slug}`}
        imageUrl={hit.img}
        highlightField="title"
        hit={hit}
        description={hit.shortDescription}
        onClick={() => onResultClick(HEADINGS.events, hit)}
      />
    ),
  },
  {
    heading: HEADINGS.news,
    indexName: 'news',
    query,
    params: centreWithDateAndViewOptionFilter(
      centre.sys.id,
      VIEW_OPTION_MEMBERS_ONLY,
      getOffsetlessTimeInCentre
    ),
    render: hit => (
      <SearchResult
        key={hit.objectID}
        href={`/${centre.slug}/news/${hit.objectID}/${hit.slug}`}
        imageUrl={hit.img}
        highlightField="title"
        hit={hit}
        description={hit.shortDescription}
        onClick={() => onResultClick(HEADINGS.news, hit)}
      />
    ),
  },
  {
    heading: HEADINGS.stories,
    indexName: 'story',
    query,
    params: centreWithDateAndViewOptionFilter(
      centre.sys.id,
      VIEW_OPTION_MEMBERS_ONLY,
      getOffsetlessTimeInCentre
    ),
    render: hit => (
      <SearchResult
        key={hit.objectID}
        href={`/${centre.slug}/story/${hit.objectID}/${hit.slug}`}
        imageUrl={hit.img}
        highlightField="title"
        hit={hit}
        description={hit.shortDescription}
        onClick={() => onResultClick(HEADINGS.stories, hit)}
      />
    ),
  },
  {
    heading: HEADINGS.centres,
    indexName: 'centre',
    query,
    params: {
      filters: `country:"${centre.country}"`,
    },
    render: hit => (
      <SearchResult
        key={hit.objectID}
        href={hit.href}
        imageUrl={hit.img}
        highlightField="title"
        hit={hit}
        onClick={() => onResultClick(HEADINGS.centres, hit)}
      />
    ),
  },
  {
    heading: `${HEADINGS.otherStores} ${centre.title}`,
    indexName: 'store',
    query,
    params: {
      aroundLatLng: `${centre.latitude}, ${centre.longitude}`,
      facetFilters: `centre:-${centre.sys.id}`,
      aroundRadius: distancePerState(centre.state), // in meters
    },
    render: hit => (
      <SearchResult
        noImage
        key={hit.objectID}
        href={hrefForStore(hit.centreSlug, hit)}
        highlightField="title"
        hit={hit}
        description={hit.centreName}
        className={style.otherStoreRow}
        onClick={() =>
          onResultClick(`${HEADINGS.otherStores} ${centre.title}`, hit)
        }
      />
    ),
  },
]

// Results:
// 1. Get rid of results with no hits
// 2. Merge results with the same header
// 3. Return the heading and render method
export const buildResults = (results, queries) =>
  results.reduce((acc, { hits }, i) => {
    if (hits.length > 0) {
      const heading = queries[i].heading
      const matchingIndex = acc.findIndex(res => res.heading === heading)
      const newHits = hits.map(queries[i].render)

      if (matchingIndex >= 0) {
        acc[matchingIndex].hits = [...acc[matchingIndex].hits, ...newHits]
      } else {
        acc.push({
          heading: queries[i].heading,
          hits: newHits,
        })
      }
    }
    return acc
  }, [])

const ESCAPE_KEYCODE = 27

// This is sort of a controller component which runs search queries against
// algolia and re-renders.
class Search extends Component {
  static propTypes = {
    centre: CentreType,
    className: PropTypes.string,
  }

  state = {
    nextClock: 1,
    visible: false,
    clock: 0,
    results: [], // List of sections. Each section has {heading, hits: [...]}.
    queryText: '',
  }

  componentDidMount() {
    const customOrigin = getClientConfigValue(CONFIG_KEYS.ALGOLIA_CUSTOM_ORIGIN)
    // TODO would like to use URL but will it be cross browser supported / transpiled?
    let opts = {}
    if (customOrigin) {
      const [protocol, host] = customOrigin.split('//')
      const hosts = [host]
      opts = { hosts, protocol }
    }

    this.client = algoliasearch(
      // It's fine to put these in the client.
      // https://www.algolia.com/doc/guides/security/api-keys/?language=php#search-only-api-key
      getClientConfigValue(CONFIG_KEYS.ALGOLIA_APPLICATION_ID),
      getClientConfigValue(CONFIG_KEYS.ALGOLIA_SEARCH_API_KEY),
      opts
    )
  }

  _handleKeyDown = event => {
    switch (event.keyCode) {
      case ESCAPE_KEYCODE:
        this.hideSearch()
        break
      default:
        break
    }
  }

  setResults(clock, results) {
    // The clock ensures we don't have race conditions from earlier search
    // results coming back after later ones
    if (clock > this.state.clock) {
      this.setState({
        clock,
        results,
      })
    }
  }

  onQueryChange = queryText => {
    this.setState({ queryText })

    // Used to make sure we always render the last
    const clock = this.state.nextClock
    this.setState({ nextClock: this.state.nextClock + 1 })

    if (queryText.trim() === '') {
      // Show no results if the query text is deleted.
      this.setResults(clock, [])
      return
    }

    const onResultClick = (heading, hit) => {
      analyticsUtils.gaTagTracking({
        event: analyticsUtils.events.SEARCH,
        eventCategory: analyticsUtils.categories.GLOBAL_SEARCH,
        eventAction: analyticsUtils.actions.SEARCH_RESULT_ClICKED,
        eventLabel: queryText,
        globalResultClicked: `${heading}/${hit.title}`,
      })
    }
    this.getOffsetlessTimeInCentre = getNowInCentreInTimeFormat(
      this.props.centre.timeZone
    )

    const queries = getQueries(
      queryText,
      this.props.centre,
      onResultClick,
      this.getOffsetlessTimeInCentre
    )
    this.client.search(queries, (err, content) => {
      if (err) {
        throw err // Is this the best thing to do??
      }

      const results = buildResults(content.results, queries)
      this.setResults(clock, results)
      const numHits = results.reduce((acc, { hits }) => acc + hits.length, 0)
      analyticsUtils.gaTagTracking({
        event: analyticsUtils.events.SEARCH,
        eventCategory: analyticsUtils.categories.GLOBAL_SEARCH,
        eventAction: analyticsUtils.actions.SEARCH_SHOWN,
        eventLabel: this.state.queryText,
        noReturnedResults: numHits,
      })
    })
  }

  hideSearch = () => {
    document.removeEventListener('keydown', this._handleKeyDown)
    window.scroll(0, 0)
    this.setState({ visible: false })
    analyticsUtils.gaTagTracking({
      event: analyticsUtils.events.SEARCH,
      eventCategory: analyticsUtils.categories.GLOBAL_SEARCH,
      eventAction: analyticsUtils.actions.CLICK_TO_CLOSE_SEARCH,
      eventLabel: this.state.queryText,
    })
  }

  showSearch = () => {
    document.addEventListener('keydown', this._handleKeyDown)
    this.setState({ visible: true })
    analyticsUtils.gaTagTracking({
      event: analyticsUtils.events.SEARCH,
      eventCategory: analyticsUtils.categories.GLOBAL_SEARCH,
      eventAction: analyticsUtils.actions.CLICK_TO_START_SEARCH,
    })
  }

  onQueryKeyDown = e => {
    if (e.keyCode === ESCAPE_KEYCODE) {
      // Escape cancels the search modal.
      this.hideSearch()
    }
  }

  onClickClear = () => {
    const clock = this.state.nextClock
    this.setState({
      queryText: '',
      nextClock: this.state.nextClock + 1,
      clock,
      results: [],
    })
    analyticsUtils.gaTagTracking({
      event: analyticsUtils.events.SEARCH,
      eventCategory: analyticsUtils.categories.GLOBAL_SEARCH,
      eventAction: analyticsUtils.actions.CLICK_TO_CLEAR_SEARCH_KEYWORD,
      eventLabel: this.state.queryText,
    })
  }

  render() {
    const { className } = this.props
    return (
      <Fragment>
        <SearchView
          results={this.state.results}
          queryText={this.state.queryText}
          isVisible={this.state.visible}
          onQueryChange={this.onQueryChange}
          onQueryKeyDown={this.onQueryKeyDown}
          onClickClose={this.hideSearch}
          onClickClear={this.onClickClear}
        />
        <button
          aria-label="Search"
          className={classNames(className, style.searchButton)}
          data-test-id="centre-search"
          onClick={this.showSearch}
        >
          <Icon type="search" />
        </button>
      </Fragment>
    )
  }
}

export default Search
